import { applyMiddleware, combineReducers, createStore } from 'redux';
import { composeWithDevTools } from 'redux-devtools-extension';
import { createLogger } from 'redux-logger';
import thunk from 'redux-thunk';
import { Topology } from '../common/config';
import { SortDirection } from '../common/types';
import { ServerApi } from '../services/server-api';
import { InviteStatusType } from '../services/server-types';
import { ModalName } from '../types/ModalNames';
import { RolesArea, RolesData } from '../types/roles';
import { InviteData, UserData, TeamData } from '../types/teamMembers';
import { ToastName } from '../types/ToastNames';
import { activeModal } from './reducers/activeModal';
import { activeToast } from './reducers/activeToast';
import { currentUser } from './reducers/currentUser';
import { roles } from './reducers/roles';
import { rolesTable } from './reducers/rolesTable';
import { teamDetails } from './reducers/teamDetails';
import { teamMembers } from './reducers/teamMembers';
import { membersTable } from './reducers/teamMembersTable';
import { AreasData } from '../types/areas';
import { areas } from './reducers/areas';
import { showTeamAndSiteOwnerHint } from './reducers/showTeamAndSiteOwnerHint';
import { roleAreas } from './reducers/roleAreas';

export type InitialState = {
  debug: boolean;
  locale: string;
  topology: Topology;
  msid?: string;
};

export type TeamMembersState = {
  invites: InviteData[];
  users: UserData[];
  teams?: TeamData[];
};

export type RolesState = RolesData;

export type RoleAreasState = RolesArea[];

export type CurrentUserState = {
  first: string;
  last: string;
  profileImage: string;
};

export type TeamDetailsState = {
  name?: string;
  imageUrl?: string;
  targetAccountId?: string;
  userId?: string;
  permissions?: Set<string>;
  loggedInAccountId?: string;
  transferOwnershipLink?: string;
  contributorLimit?: number;
  shouldEnforceContributors?: boolean;
  loadingTeammates?: boolean;
};

export type AreasState = AreasData;

export type State = InitialState & {
  msid?: string,
  activeModal: {
    modalName: ModalName;
    params: any;
  };
  activeToast: {
    toastName: ToastName;
    params: any;
  };
  membersTable: {
    query: string;
    filters: {
      roles: string[];
      status: InviteStatusType;
      namesAndEmails: string;
    }
    sort: {
      column: string;
      direction: SortDirection;
    },
  };
  rolesTable: {
    query: string;
  };
  teamMembers: TeamMembersState;
  roles: RolesState;
  roleAreas: RoleAreasState;
  teamDetails: TeamDetailsState;
  currentUser: CurrentUserState;
  areas: AreasState;
  showTeamAndSiteOwnerHint: boolean;
};

export type TeamsServices = {
  serverApi: ServerApi;
};

export const configureStore = (
  initialState: InitialState,
  services: TeamsServices
) => {
  const selfReducer = (state = null) => state;

  const { debug } = initialState;

  const rootReducer = combineReducers({
    debug: selfReducer,
    locale: selfReducer,
    topology: selfReducer,
    msid: selfReducer,
    activeModal,
    activeToast,
    membersTable,
    teamMembers,
    roles,
    roleAreas,
    rolesTable,
    teamDetails,
    currentUser,
    areas,
    showTeamAndSiteOwnerHint,
  });

  const appliedMiddleware = getMiddlewares(services, debug);

  return createStore(rootReducer, initialState, appliedMiddleware);
};

const getMiddlewares = (services: TeamsServices, isDebug: boolean) => {
  const thunkMiddleware = thunk.withExtraArgument(services);
  return isDebug
    ? composeWithDevTools({ serialize: true })(applyMiddleware(thunkMiddleware, createLogger()))
    : applyMiddleware(thunkMiddleware);
};
